import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import {
  GridActionToolbar,
  GridActionToolbarAction
} from './grid-action-toolbar';

export type AppGridColumn<Row> = {
  key: string;
  header: React.ReactNode;
  renderCell: (row: Row) => React.ReactNode;
};

export type AppGridProps<Row> = {
  rows: Row[];
  columns: AppGridColumn<Row>[];
  getRowId: (row: Row) => string;
  selectedRowIds?: string[];
  onSelectedRowIdsChange?: (selectedRowIds: string[]) => void;
  multiSelect?: boolean;
  isRowSelected?: (row: Row) => boolean;
  onRowClick?: (row: Row, event: React.MouseEvent<HTMLTableRowElement>) => void;
  leadingHeaderCell?: React.ReactNode;
  renderLeadingCell?: (row: Row) => React.ReactNode;
  actions?: GridActionToolbarAction[];
  paperSx?: SxProps<Theme>;
};

export const AppGrid = <Row,>({
  rows,
  columns,
  getRowId,
  selectedRowIds,
  onSelectedRowIdsChange,
  multiSelect = false,
  isRowSelected,
  onRowClick,
  leadingHeaderCell,
  renderLeadingCell,
  actions,
  paperSx
}: AppGridProps<Row>) => {
  const handleSelectionChange = (
    rowId: string,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => {
    if (!selectedRowIds || !onSelectedRowIdsChange) return;

    const isMultiClick = multiSelect && (event.ctrlKey || event.metaKey);

    if (isMultiClick) {
      const next = selectedRowIds.includes(rowId)
        ? selectedRowIds.filter((id) => id !== rowId)
        : [...selectedRowIds, rowId];
      onSelectedRowIdsChange(next);
      return;
    }

    if (selectedRowIds.length === 1 && selectedRowIds[0] === rowId) return;
    onSelectedRowIdsChange([rowId]);
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: '100%',
        borderRadius: 3,
        border: (theme) =>
          `1px solid ${theme.designSystem.surface.panelBorder}`,
        background: (theme) => theme.designSystem.surface.gridPanelBackground,
        boxShadow: (theme) => theme.designSystem.surface.panelShadow,
        overflow: 'hidden',
        ...paperSx
      }}
    >
      <TableContainer
        sx={{
          flex: 1,
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          background: (theme) => theme.designSystem.surface.gridPanelBackground
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: '100%',
            width: 'max-content'
          }}
        >
          <TableHead
            sx={{
              height: '2.8rem',
              '& .MuiTableCell-root': {
                backgroundColor: (theme) =>
                  theme.designSystem.surface.gridHeaderBackground,
                borderBottom: (theme) =>
                  `1px solid ${theme.designSystem.surface.headerDivider}`,
                fontWeight: 600
              }
            }}
          >
            <TableRow>
              {leadingHeaderCell ? (
                <TableCell padding="checkbox">{leadingHeaderCell}</TableCell>
              ) : null}
              {columns.map((column) => (
                <TableCell key={column.key}>{column.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const rowId = getRowId(row);
              const isSelected =
                selectedRowIds?.includes(rowId) ??
                isRowSelected?.(row) ??
                false;

              return (
                <TableRow
                  key={rowId}
                  hover
                  onClick={
                    onRowClick || onSelectedRowIdsChange
                      ? (event) => {
                          onRowClick?.(row, event);
                          handleSelectionChange(rowId, event);
                        }
                      : undefined
                  }
                  selected={isSelected}
                  sx={{
                    cursor:
                      onRowClick || onSelectedRowIdsChange
                        ? 'pointer'
                        : 'default',
                    '&:hover td': {
                      backgroundColor: (theme) =>
                        theme.designSystem.state.rowHover
                    },
                    '&.Mui-selected td': {
                      background: (theme) =>
                        theme.designSystem.state.rowSelectedGradient,
                      borderTop: (theme) =>
                        `1px solid ${theme.designSystem.state.rowSelectedBorder}`,
                      borderBottom: (theme) =>
                        `1px solid ${theme.designSystem.state.rowSelectedBorder}`
                    },
                    '&.Mui-selected td:first-of-type': {
                      boxShadow: (theme) =>
                        `inset 2px 0 0 ${theme.designSystem.state.rowSelectedInset}`
                    },
                    '&.Mui-selected td:last-of-type': {
                      borderRight: (theme) =>
                        `1px solid ${theme.designSystem.state.rowSelectedRightBorder}`
                    },
                    '&.Mui-selected:hover td': {
                      background: (theme) =>
                        theme.designSystem.state.rowSelectedHoverGradient
                    },
                    '& td': {
                      height: '2.8rem',
                      verticalAlign: 'middle',
                      borderBottom: (theme) =>
                        `1px solid ${theme.designSystem.surface.rowDivider}`,
                      transition:
                        'background-color 0.2s ease, border-color 0.2s ease'
                    },
                    '&:last-child td': {
                      borderBottom: 0
                    }
                  }}
                >
                  {renderLeadingCell ? (
                    <TableCell
                      padding="checkbox"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renderLeadingCell(row)}
                    </TableCell>
                  ) : null}
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      {column.renderCell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {actions?.length ? <GridActionToolbar actions={actions} /> : null}
    </Paper>
  );
};
